import React, { useEffect, useState } from 'react';
import Input from '../../../../components/atoms/input';
import { Box, Grid } from '@material-ui/core';
import ButtonComponent from '../../../../components/atoms/button';
import {
  freightTypeEnum,
  useConfigureFreight,
} from '../../../../hooks/freights/useConfigureFreight';
import { ApplicationState } from '../../../../store';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import SelectCustom from '../../../atoms/selectCustom';
import useGetDeliveriesRequests from '../../../../hooks/delivery-requests';
import FreightConfigurationTable from '../../../../modules/freight/FreightConfigurationTable';
import useGetDrivers from '../../../../hooks/driver/useGetDrivers';
import DatePickerCustom from '../../../atoms/datePickerCustom';
import TimePickerCustom from '../../../atoms/timePickerCustom';
import {
  DriverTypeEnum,
  FreightRequest,
} from '../../../../store/ducks/freight/types';
import { Row } from '../../../../hooks/carts/useNearGetCarts';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import { ReactComponent as CloseRedIcon } from '../../../../assets/icons/icon-cross-circle-red.svg';
import InputDisabledOrange from '../../../atoms/inputDisabledOrange';
import InputBase from '@mui/material/InputBase';
import commons from '../../../../assets/styles/commons';
import Paper from '@mui/material/Paper';
import colors from '../../../../assets/styles/colors';
import RadioGroupCustom from '../../../atoms/radioGroupCustom';
import moment from 'moment';
import ModalCustom from '../../../atoms/modalCustom';
import { DriverStatus } from '../../../../hooks/driver/useGetDriverStatistics';
import { SelectChangeEvent } from '@mui/material';
import useGetPrivateVehicles, {
  Vehicle,
} from '../../../../hooks/vehicle/useGetPrivateVehicle';
import { useLocation, useParams } from 'react-router-dom';

interface ConfigureFreightProps {
  onNextStep: () => void;
  onPrevStep: () => void;
  uploadedFiles: any[];
  setDisableVehicleType: React.Dispatch<React.SetStateAction<boolean>>;
  disableVehicleType: boolean;
  mode?: 'edit' | 'create';
}

interface f {
  vehicleType: string;
  loadNumber: string;
  extraCarts: Row[];
  driverId: string;
  vehicleId: string;
  vehicleTypeId: string;
  originalTotalWeight: number;
  driverName: string;
  phone: string;
  email: string;
  pickUpDate: string;
  pickUpTime: string;
  deliveryLimitDate: string;
  vehicleTypePrice: number;
  isInternalFreight?: boolean;
}

export interface FreightDetails extends f, FreightRequest {}

const ConfigureFreight: React.FC<ConfigureFreightProps> = ({
  onNextStep,
  onPrevStep,
  uploadedFiles,
  setDisableVehicleType,
  disableVehicleType,
}) => {
  const location = useLocation();
  const { id: freightRequestIdParam } = useParams<{ id: string }>();
  const mode = freightRequestIdParam ? 'edit' : 'create';
  const {
    control,
    getValues,
    reset,
    setValue,
    formState,
    watch,
    trigger,
    clearErrors,
  } = useFormContext<FreightDetails>();

  const [modalOpen, setModalOpen] = useState(false);

  const freightDetails = getValues();

  const freightType = watch('freightType', freightTypeEnum.PUBLIC);
  const vehicleIdWatched = watch('vehicleId');
  const vehicleTypeIdWatched = watch('vehicleTypeId');

  const [open, setOpen] = React.useState(false);
  const [selectPackLoadWeight, setSelectPackLoadWeight] = useState(0);
  const [isInternalFreight, setIsInternalFreight] = useState(false);

  const { execute: configureFreight, loading: configureFreightLoading } = useConfigureFreight();
  const { execute: getDeliveryRequest } = useGetDeliveriesRequests();
  const { execute: getDrivers } = useGetDrivers();
  const { execute: getVehicles, loading } = useGetPrivateVehicles();

  const { vehicleTypes } = useSelector(
    (state: ApplicationState) => state.tables,
  );

  const vehicleType = vehicleTypes.rows.find(
    vehicleType => vehicleType.vehicleTypeId === freightDetails.vehicleTypeId,
  )?.vehicleType;
  const [inputType] = useState('text');
  const [drivers, setDrivers] = useState<any>({
    rows: [],
    totalRecords: 0,
    page: 1,
  });
  const [vehicles, setVehicles] = useState<any>([]);
  const freighWeightPassedSelectedVehicleWeight =
    selectPackLoadWeight < freightDetails.totalWeight;

  useEffect(() => {
    if (mode === 'edit') {
      const params = new URLSearchParams(location.search);
      const id = params.get('freightRequestId');
      const stateId = (location as any)?.state?.initialValues?.freightRequestId;
      const resolvedId = id || stateId;

      if (resolvedId && getValues('freightRequestId') !== resolvedId) {
        setValue('freightRequestId', resolvedId, {
          shouldValidate: true,
          shouldDirty: false,
        });
        trigger('freightRequestId');
      }
    }
  }, [
    mode,
    location.search,
    (location as any)?.state?.initialValues?.freightRequestId,
    getValues,
    setValue,
    trigger,
  ]);

  const handleConfigureFreight = async () => {
    const freightDetails = getValues();

    const isValidUuid = (value?: string) =>
      !!value &&
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
        value,
      );

    if (!isValidUuid(freightDetails.freightRequestId)) {
      alert('ID do frete (freightRequestId) inválido ou não informado.');
      return;
    }

    const freightType =
      isInternalFreight && freightDetails.freightType == freightTypeEnum.PRIVATE
        ? freightTypeEnum.INTERNAL
        : freightDetails.freightType;

    await configureFreight({
      driverId: freightDetails.driverId,
      freightRequestId: freightDetails.freightRequestId,
      totalWeight: freightDetails.totalWeight,
      totalDeliveries: freightDetails.totalDeliveries,
      numberOfHelpers: freightDetails.numberOfHelpers || 0,
      totalValue: freightDetails.totalValue,
      deliveryLimitDate: freightDetails.deliveryLimitDate,
      pickUpDate: freightDetails.pickUpDate,
      pickUpTime: freightDetails.pickUpTime,
      freightType,
      vehicleTypeId: freightDetails.vehicleTypeId,
      extraCartIds: freightDetails.extraCarts?.map(cart => cart.cartId),
      vehicleId: freightDetails.vehicleId,
    });

    reset({
      ...freightDetails,
      totalKilometers: freightDetails.totalKilometers,
      freightType,
      freightCost: freightDetails.freightCost,
      totalWeight: freightDetails.totalWeight,
      totalDeliveries: freightDetails.totalDeliveries,
    });
  };

  const handleNext = async () => {
    await handleConfigureFreight();
    onNextStep();
  };

  const handlePrev = () => {
    reset({});
    onPrevStep();
  };

  const handleOpenModalBack = () => {
    setModalOpen(true);
  };

  const handleSelectPackLoadWeight = (vehicleTypeId: string) => {
    setOpen(true);

    const packWeight = vehicleTypes.rows.find(
      option => option.vehicleTypeId === vehicleTypeId,
    )?.packLoadWeight;
    setSelectPackLoadWeight(Number(packWeight));
  };

  useEffect(() => {
    if (!vehicleIdWatched || !vehicles.length) return;

    const found = vehicles.find(
      (vehicle: Vehicle) => vehicle.vehicleId === vehicleIdWatched,
    );
    const vTypeId = found?.vehicleType?.vehicleTypeId;

    if (vTypeId && vTypeId !== vehicleTypeIdWatched) {
      setValue('vehicleTypeId', vTypeId, {
        shouldValidate: true,
        shouldDirty: true,
      });
      handleSelectPackLoadWeight(vTypeId);
      updatedVehicleType(vTypeId);
    }
  }, [vehicleIdWatched, vehicles, vehicleTypeIdWatched, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const driverType = isInternalFreight
          ? DriverTypeEnum.PRIVATE
          : DriverTypeEnum.AUTONOMOUS;
        const data = await getDrivers({
          page: 1,
          type: driverType,
          status: DriverStatus.ACTIVE,
        });
        setDrivers(data);
      } catch (error) {
        console.error('Erro ao obter os dados:', error);
      }
    };

    fetchData();
  }, [isInternalFreight]);

  useEffect(() => {
    const fetchDeliveryRequestData = async () => {
      try {
        const data = await getDeliveryRequest(freightDetails.freightRequestId);

        reset({
          ...data,
          originalTotalWeight: data?.totalWeight,
          vehicleType: vehicleType,
          loadNumber: data?.deliveryRequests[0]?.loadNumber,
          freightCost: data?.freightCost,
        });
      } catch (error) {
        console.error('Erro ao buscar dados da requisição de entrega:', error);
      }
    };

    if (freightDetails.freightRequestId) {
      fetchDeliveryRequestData();
    }
  }, [freightDetails.freightRequestId]);

  useEffect(() => {
    if (!vehicleTypes?.rows?.length) return;
    if (vehicleTypeIdWatched) {
      handleSelectPackLoadWeight(vehicleTypeIdWatched);
      updatedVehicleType(vehicleTypeIdWatched);
    }
  }, [vehicleTypeIdWatched, vehicleTypes]);

  const updatedVehicleType = (vehicleTypeId: string) => {
    const vehicleType = vehicleTypes.rows.find(
      (vehicleType: { vehicleTypeId: string }) =>
        vehicleType.vehicleTypeId === vehicleTypeId,
    );
    setValue('vehicleType', vehicleType?.vehicleType ?? '');
  };

  const nextButtonIsEnable = () => {
    const freightRequestId = getValues('freightRequestId');
    const isValidUuid = (value?: string) =>
      !!value &&
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
        value,
      );
    const baseValid = formState.isValid && isValidUuid(freightRequestId);

    // If freight is PRIVATE and driver is third-party (isInternalFreight === false),
    // enable Next when the form is valid and a driver is selected, regardless of vehicle/weight.
    if (freightType === freightTypeEnum.PRIVATE && !isInternalFreight) {
      const driverSelected = !!getValues('driverId');
      return baseValid && driverSelected;
    }

    return (
      baseValid &&
      (isInternalFreight || !freighWeightPassedSelectedVehicleWeight)
    );
  };

  useEffect(() => {
    if (isInternalFreight && freightType === freightTypeEnum.PRIVATE) {
      clearErrors('vehicleTypeId');
      getVehicleOptions();
    } else {
      setDisableVehicleType(false);
    }
  }, [isInternalFreight, freightType]);

  const getVehicleTypeOptions = () => {
    if (!vehicleTypes?.rows?.length) return [];
    return vehicleTypes.rows.map(option => ({
      value: option.vehicleTypeId,
      label: option.vehicleType + ' - ' + option.packLoadWeight + ' Kg',
    }));
  };

  const getVehicleOptions = async () => {
    const privateVehicles = await getVehicles({
      page: 1,
      rowsPerPage: 1000,
    });
    setVehicles(privateVehicles?.rows);
  };

  return (
    <>
      <ModalCustom
        open={modalOpen}
        onCancelClick={() => setModalOpen(false)}
        label={'Você tem certeza que deseja voltar?'}
        children={
          'Ao retornar, todos os dados não salvos serão perdidos e será necessário importar um novo frete.'
        }
        onClickPublish={handlePrev}
        ButtonOutlinePrimary="Não"
        ButtonPrimary="Sim"
      />
      {mode !== 'edit' && (
        <Box>
          <Paper
            component="form"
            elevation={0}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '40px',
              border: '1px solid #8F9BB3',
              backgroundColor: colors.secondary.softBlue,
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1, fontFamily: commons.fontFamily }}
              placeholder={
                uploadedFiles
                  ? uploadedFiles.join(', ')
                  : 'Anexe os fretes aqui....'
              }
              type={inputType}
              readOnly
            />
            <ButtonComponent theme="ghost" disabled>
              Arquivos Selecionados
            </ButtonComponent>
          </Paper>
        </Box>
      )}

      <hr />
      <Box component={'div'}>
        <RadioGroupCustom
          title="Tipo de frete"
          selectedValue={freightType}
          onChange={value => {
            setValue('freightType', value, { shouldValidate: true });
            if (value === 'Privado') {
              setIsInternalFreight(true);
            } else {
              setIsInternalFreight(false);
            }
          }}
          options={[
            { value: freightTypeEnum.PUBLIC, label: 'Público' },
            { value: freightTypeEnum.PRIVATE, label: 'Privado' },
          ]}
        />

        {freightType === freightTypeEnum.PRIVATE && (
          <RadioGroupCustom
            title="Motorista"
            selectedValue={isInternalFreight ? 'true' : 'false'}
            onChange={value => {
              setValue('driverId', undefined as any);
              setIsInternalFreight(value === 'true');
              setValue('isInternalFreight', value === 'true', {
                shouldValidate: true,
              });
              trigger('driverId');
              trigger('vehicleId');
              trigger();
            }}
            options={[
              { value: 'true', label: 'Da casa' },
              { value: 'false', label: 'Tercerizado' },
            ]}
          />
        )}

        <hr />
        {freighWeightPassedSelectedVehicleWeight && (
          <Collapse in={open}>
            <Alert
              icon={false}
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  <CloseRedIcon />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              <strong>
                O peso total do frete está ultrapassando o limite máximo do
                veículo selecionado.
              </strong>
            </Alert>
          </Collapse>
        )}

        <Grid container alignContent="stretch" spacing={2}>
          <Grid item xs={4}>
            {freightType === freightTypeEnum.PRIVATE && (
              <Controller
                name="driverId"
                control={control}
                render={({ field: { value } }) => (
                  <SelectCustom
                    size="small"
                    label={'Motorista'}
                    defaultValue={value}
                    value={value}
                    options={
                      drivers?.rows?.map(
                        (option: {
                          driverId: string;
                          username: string;
                          vehicleOwnerships?: {
                            vehicle?: {
                              plate?: string;
                            };
                          }[];
                        }) => {
                          const plate =
                            option?.vehicleOwnerships?.length &&
                            option?.vehicleOwnerships[0].vehicle?.plate;
                          return {
                            value: option.driverId,
                            label: plate
                              ? `${option.username} - ${plate.toUpperCase()}`
                              : `${option.username}`,
                          };
                        },
                      ) || []
                    }
                    onChange={(event: SelectChangeEvent<any>) => {
                      setValue('driverId', event?.target?.value);
                      trigger();
                    }}
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={4}>
            {freightType === freightTypeEnum.PRIVATE && isInternalFreight && (
              <Controller
                name="vehicleId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SelectCustom
                    size="small"
                    label={'Veículo'}
                    defaultValue={value}
                    value={value}
                    options={
                      vehicles?.map((option: any) => ({
                        value: option.vehicleId,
                        label: option.model + ' - ' + option.plate,
                      })) || []
                    }
                    onChange={(event: SelectChangeEvent<any>) => {
                      const vehicleId = event?.target?.value;
                      onChange(vehicleId);
                      trigger();
                    }}
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={1} />
          <Grid item xs={4}>
            <Controller
              name="numberOfHelpers"
              control={control}
              render={({ field: { onChange } }) => (
                <Input
                  name="numberOfHelpers"
                  control={control}
                  label={'Ajudantes necessários'}
                  onChange={event =>
                    onChange(Number((event.target as HTMLInputElement).value))
                  }
                  size="small"
                  maxLength={2}
                />
              )}
            />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name="vehicleTypeId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SelectCustom
                  size="small"
                  label={'Tipo de veículo'}
                  defaultValue={value || ''}
                  value={value || ''}
                  disabled={disableVehicleType}
                  options={getVehicleTypeOptions()}
                  onChange={event => {
                    const vehicleTypeIdValue = (
                      event as React.ChangeEvent<{ value: unknown }>
                    )?.target?.value as string;

                    onChange(vehicleTypeIdValue);
                    handleSelectPackLoadWeight(vehicleTypeIdValue);
                    updatedVehicleType(vehicleTypeIdValue as string);
                    trigger();
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={4}>
            <InputDisabledOrange
              name="totalWeight"
              control={control}
              size="small"
              mask={(value): string => {
                return value.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }}
              label={'Peso total'}
              endAdornment={'kg'}
            />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name="pickUpDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePickerCustom
                  placeholder="31/10/2023"
                  label="Data de retirada"
                  errorMessage={formState.errors['pickUpDate']?.message}
                  value={value ? moment(value, 'YYYY-MM-DD') : null}
                  onChange={(newDate: any) => {
                    onChange(newDate ? newDate.format('YYYY-MM-DD') : null);
                    trigger();
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name="pickUpTime"
              control={control}
              render={({ field: { value, onChange } }) => {
                const parsed = value ? moment(value, 'HH:mm', true) : null;
                const safeValue = parsed && parsed.isValid() ? parsed : null;
                return (
                  <TimePickerCustom
                    label="Horário de retirada"
                    errorMessage={formState.errors['pickUpTime']?.message}
                    value={safeValue}
                    onChange={(newTime: any) => {
                      // debug: garantir o que está vindo do picker
                      if (newTime && typeof newTime?.seconds === 'function')
                        newTime.seconds(0);
                      if (
                        newTime &&
                        typeof newTime?.milliseconds === 'function'
                      )
                        newTime.milliseconds(0);
                      if (newTime && newTime.isValid()) {
                        const formatted = newTime.format('HH:mm');
                        // eslint-disable-next-line no-console
                        console.log('[pickUpTime:onChange]', { formatted });
                        onChange(formatted);
                      } else {
                        // eslint-disable-next-line no-console
                        console.log('[pickUpTime:onChange] invalid -> null');
                        onChange(null);
                      }
                    }}
                  />
                );
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name="deliveryLimitDate"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DatePickerCustom
                  placeholder="31/10/2023"
                  label="Data de Limite de Entrega"
                  value={value ? moment(value, 'YYYY-MM-DD') : null}
                  errorMessage={formState.errors['deliveryLimitDate']?.message}
                  onChange={(newDate: any) => {
                    onChange(newDate ? newDate.format('YYYY-MM-DD') : null);
                    trigger();
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <hr />

        <FreightConfigurationTable />

        <Grid container justifyContent="space-between">
          <ButtonComponent
            theme="outline-primary"
            justifyContent="flex-start"
            mt={5}
            onClick={handleOpenModalBack}
          >
            Voltar
          </ButtonComponent>
          <ButtonComponent
            theme="primary"
            justifyContent="flex-end"
            mt={5}
            onClick={handleNext}
            disabled={!nextButtonIsEnable() || configureFreightLoading}
            loading={configureFreightLoading}
          >
            Próximo
          </ButtonComponent>
        </Grid>
      </Box>
    </>
  );
};

export default ConfigureFreight;
